import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { DillButton } from '../../../general/Dill/DillButton';
import { Plus, Bank, CreditCard, Warning } from 'phosphor-react';
import AppAddPaymentMethodModal from '../../../general/AppAddPaymentMethodModal/AppAddPaymentMethodModal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DillPaymentMethod, SupplierForBuyer, appDelay } from '@dill/dill-shared';
import {
  deletePaymentMethodByMainBuyer,
  getMainBuyerPaymentMethods,
} from '../../../../redux/services/paymentsService';
import AppVerifyDwollaMicroTransactionsModal from '../../../general/AppVerifyDwollaMicroTransactionsModal/AppVerifyDwollaMicroTransactionsModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { useBuyerUserPermissions } from '../../../../utils/hooks/useBuyerUserPermissions';
import { Trash } from 'react-feather';
import { closeMessageModal, openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { getGroupedBuyersSuppliers } from '../../../../redux/services/buyersService';
import { updateSelectedSupplierForBuyer } from '../../../../redux/globalSlices/invoiceSlice';

export const BuyerPaymentMethods = ({ layout = 'row' }: { layout?: 'row' | 'column' }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { paymentMethods } = useAppSelector((state) => state.payments);
  const { user } = useAppSelector((state) => state.auth);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const [isOpenActionOption, setOpenActionOptions] = useState('');
  const { selectedSupplierForBuyer } = useAppSelector((state) => state.invoice);
  const { selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);
  const [isAppAddPaymentMethodModalOpen, setIsAppAddPaymentMethodModalOpen] = useState(false);
  const [
    isAppVerifyDwollaMicroTransactionsModalOpen,
    setIsAppVerifyDwollaMicroTransactionsModalOpen,
  ] = useState(false);
  const [
    selectedPaymentMethodToVerifyMicroTransactions,
    setSelectedPaymentMethodToVerifyMicroTransactions,
  ] = useState<DillPaymentMethod>();
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const { isPreviewMode, previewMainBuyer } = useAppSelector((state) => state.preview);
  const { buyerUserCanPay } = useBuyerUserPermissions();

  const toggleDropdown = (value: string) => {
    setOpenActionOptions(value);
  };
  const handleDelete = () => {
    setOpenActionOptions('');
  };
  const handleAddPayment = () => {
    setIsAppAddPaymentMethodModalOpen(true);
  };

  // Opens the verify-deposits modal from the `?paymentMethodId=` URL param. The param is set
  // both by the "Verify Deposits" button below and by deep links (e.g. verification emails).
  useEffect(() => {
    if (!location.pathname.startsWith('/companies')) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const paymentMethodId = searchParams.get('paymentMethodId');
    // Bail out when there is no param so a payment-methods refetch (e.g. on close) can't
    // re-open an already-closed modal.
    if (!paymentMethodId) {
      return;
    }
    const paymentMethod = paymentMethods.find((item) => item.id === paymentMethodId);
    if (paymentMethod) {
      setSelectedPaymentMethodToVerifyMicroTransactions(paymentMethod);
      setIsAppVerifyDwollaMicroTransactionsModalOpen(true);
    }
  }, [location.pathname, location.search, paymentMethods]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(
        getGroupedBuyersSuppliers({
          mainBuyerId: selectedMainBuyerId,
          weeksLimit: 2,
        })
      );
    }
  }, [selectedMainBuyerId]);

  const buyerForMainBuyer = useMemo(() => {
    const buyer = user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
    return buyer;
  }, [selectedSupplierForBuyer, selectedMainBuyer, user?.userBuyers]);

  const handleDeletePaymentMethod = async (paymentMethod: DillPaymentMethod) => {
    const isBankAccount = paymentMethod.type === 'BANK_ACCOUNT';
    const warningMessage = isBankAccount
      ? 'This action cannot be undone. If you delete a bank method and decide to re-add the same one, you will need to wait 48 hours.'
      : 'This action can not be undone.';

    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Delete',
        modalType: 'WARN',
        title: `Are you sure you want to delete this payment method?`,
        subTitle: warningMessage,
        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Deleting',
              subTitle: 'Deleting payment method',
            })
          );
          const deletePaymentMethodResults = await dispatch(
            deletePaymentMethodByMainBuyer({
              mainBuyerId: selectedMainBuyerId || '',
              paymentMethodId: paymentMethod.id,
            })
          );
          if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/rejected') {
            let errorMessage = '';
            if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/rejected') {
              errorMessage = deletePaymentMethodResults.payload as any;
            }
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error deleting payment method',
                subTitle: `${errorMessage} Contact (628) 304-0931 for customer support`,
              })
            );
          } else if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/fulfilled') {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: 'Payment method deleted',
                subTitle: `Payment method deleted successfully`,
                onClose: () => {
                  dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
                },
              })
            );
          } else {
            closeMessageModal();
          }
        },
      })
    );
  };

  useEffect(() => {
    let newSupplier: SupplierForBuyer | null = null;

    if (selectedBuyerSuppliers.length > 0 && location.pathname.includes('/payments')) {
      const supplier: SupplierForBuyer = selectedBuyerSuppliers[0];
      if (supplier) {
        newSupplier = supplier;
      } else {
        newSupplier = null;
      }
      dispatch(updateSelectedSupplierForBuyer(newSupplier));
    }
  }, [selectedMainBuyerId, selectedBuyerSuppliers]);

  const filteredPaymentMethods = paymentMethods.filter((pm) => {
    if (isPreviewMode) {
      return false;
    }
    if (
      pm.provider === 'DWOLLA' &&
      buyerForMainBuyer &&
      buyerForMainBuyer.disabledPaymentTypes &&
      buyerForMainBuyer.disabledPaymentTypes.includes('BANK')
    ) {
      return false;
    }
    if (
      pm.cardType &&
      pm.cardType === 'CREDIT' &&
      buyerForMainBuyer &&
      buyerForMainBuyer.disabledPaymentTypes &&
      buyerForMainBuyer.disabledPaymentTypes.includes('CREDIT_CARD')
    ) {
      return false;
    }
    if (
      pm.cardType &&
      pm.cardType !== 'CREDIT' &&
      buyerForMainBuyer &&
      buyerForMainBuyer.disabledPaymentTypes &&
      buyerForMainBuyer.disabledPaymentTypes.includes('DEBIT_CARD')
    ) {
      return false;
    }
    if (pm.type === 'CREDIT_CARD' && selectedSupplierForBuyer?.isOptoutCardPayments) {
      return false;
    }
    if (pm.cardType && pm.cardType !== 'CREDIT' && selectedSupplierForBuyer?.isDebitCardDisabled) {
      return false;
    }
    if (pm.provider === 'DWOLLA') {
      return true;
    }
    if (
      pm.provider === 'STRIPE' &&
      (selectedSupplierForBuyer?.cardPaymentProvider === 'STRIPE' ||
        !selectedSupplierForBuyer?.cardPaymentProvider)
    ) {
      return true;
    }
    if (pm.provider === 'FISERV' && selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV') {
      return true;
    }
    return false;
  });

  return (
    <div className="flex flex-col overflow-hidden">
      <AppAddPaymentMethodModal
        open={isAppAddPaymentMethodModalOpen}
        handleClose={() => {
          dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
          setIsAppAddPaymentMethodModalOpen(false);
        }}
        isDebitCardDisabled={
          selectedSupplierForBuyer?.isDebitCardDisabled ||
          (buyerForMainBuyer &&
            buyerForMainBuyer?.disabledPaymentTypes &&
            buyerForMainBuyer.disabledPaymentTypes?.includes('DEBIT_CARD'))
            ? true
            : false
        }
        isCreditCardDisabled={
          buyerForMainBuyer &&
          buyerForMainBuyer?.disabledPaymentTypes &&
          buyerForMainBuyer?.disabledPaymentTypes?.includes('CREDIT_CARD')
            ? true
            : false
        }
        isBankDisabled={
          buyerForMainBuyer &&
          buyerForMainBuyer?.disabledPaymentTypes &&
          buyerForMainBuyer?.disabledPaymentTypes?.includes('BANK')
            ? true
            : false
        }
        paymentOptionsToShow={
          selectedSupplierForBuyer?.isOptoutCardPayments
            ? 'BANK'
            : selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV'
            ? 'BANK_AND_FISERV_CARD'
            : 'ALL'
        }
        userType="buyer"
      />
      <AppVerifyDwollaMicroTransactionsModal
        selectedPaymentMethod={selectedPaymentMethodToVerifyMicroTransactions}
        open={isAppVerifyDwollaMicroTransactionsModalOpen}
        handleClose={() => {
          dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
          setSelectedPaymentMethodToVerifyMicroTransactions(undefined);
          setIsAppVerifyDwollaMicroTransactionsModalOpen(false);
          navigate(location.pathname);
        }}
      />

      {/* Header */}
      <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center items-start gap-1.5 sm:gap-0 mb-1.5">
        <div className="font-semibold text-sm text-gray-900">Payment Methods</div>
        {user?.roles.includes('BUYER') && !isPreviewMode && (
          <div data-tooltip-id={!buyerUserCanPay ? 'no_pay_permission' : undefined}>
            <DillButton
              label="Add Payment Method"
              icon={<Plus size={14} color="#FFFFFF" />}
              onClick={handleAddPayment}
              isDisabled={!buyerUserCanPay}
            />
          </div>
        )}
      </div>

      {/* Scrollable card row */}
      {filteredPaymentMethods.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 px-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-center w-full">
          <CreditCard size={32} color="#CBD5E1" />
          <span className="text-sm font-medium">No payment methods added yet</span>
        </div>
      ) : (
        <div className="relative">
          {/* Fade-out hint at right edge - only meaningful when cards scroll horizontally */}
          {layout === 'row' && (
            <div className="pointer-events-none absolute top-0 right-0 h-full w-12 bg-gradient-to-r from-transparent to-slate-50 rounded-r-xl z-10" />
          )}
          <div
            className={
              layout === 'column'
                ? 'flex flex-col gap-3'
                : `flex flex-row gap-3.5 overflow-x-auto pb-2 pr-12
              [&::-webkit-scrollbar]:h-1
              [&::-webkit-scrollbar-track]:bg-slate-100
              [&::-webkit-scrollbar-track]:rounded
              [&::-webkit-scrollbar-thumb]:bg-slate-300
              [&::-webkit-scrollbar-thumb]:rounded
              [&::-webkit-scrollbar-thumb:hover]:bg-slate-400`
            }>
            {filteredPaymentMethods.map((paymentMethod, i) => {
              const isBank = paymentMethod.type === 'BANK_ACCOUNT';
              const isCredit = paymentMethod.cardType === 'CREDIT';
              const isUnverified =
                paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                paymentMethod.status === 'UNVERIFIED';

              const iconColor = isBank
                ? 'text-blue-500'
                : isCredit
                ? 'text-violet-500'
                : 'text-orange-400';

              const typeLabel = isBank
                ? 'Bank Transfer'
                : isCredit
                ? 'Credit Card'
                : 'Debit / Other';

              return (
                <div
                  key={`buyer_payment_${paymentMethod.id}`}
                  className={`flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-150 hover:shadow-md ${
                    layout === 'column' ? 'w-full' : 'flex-shrink-0 w-[300px]'
                  }`}>
                  {/* Main row */}
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <div className={`shrink-0 ${iconColor}`}>
                      {isBank ? (
                        <Bank size={26} weight="duotone" />
                      ) : (
                        <CreditCard size={26} weight="duotone" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-800 truncate">
                        {paymentMethod.paymentMethodName}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{typeLabel}</div>
                    </div>
                    <DillButton
                      variant="icon-red"
                      icon={<Trash size={14} />}
                      onClick={() => handleDeletePaymentMethod(paymentMethod)}
                    />
                  </div>

                  {/* Unverified footer */}
                  {isUnverified && (
                    <div
                      data-tooltip-id={`payment-method-tooltip_${i}`}
                      className="flex items-center justify-between gap-2 px-4 py-2.5 bg-amber-50 border-t border-amber-100">
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                        <Warning size={13} weight="fill" />
                        Pending verification
                      </span>
                      <ReactTooltip
                        id={`payment-method-tooltip_${i}`}
                        place="top"
                        variant="dark"
                        opacity={'100%'}
                        style={{
                          display: 'flex',
                          background: '#334155',
                          width: '300px',
                          zIndex: '70',
                          borderRadius: '8px',
                          alignItems: 'center',
                          justifyItems: 'center',
                          fontWeight: '600',
                        }}>
                        {paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ? (
                          <span className="text-xs">
                            Two micro-deposits have arrived in your account. Please check for these
                            deposits in your account and verify them in order to use this bank
                            method. This is a one-time setup for this bank account.
                          </span>
                        ) : (
                          <span className="text-xs">
                            Two micro-deposits were sent to this bank account. They should arrive
                            1-2 business days from the day it was added. Once you see these deposits
                            in your account, please verify them in order to use this bank method.
                            This is a one-time setup for this bank account.
                          </span>
                        )}
                      </ReactTooltip>
                      <DillButton
                        label="Verify Deposits"
                        isDisabled={paymentMethod.status !== 'MICRO_DEPOSITS_VERIFICATION_PENDING'}
                        onClick={() => {
                          navigate(`${location.pathname}?paymentMethodId=${paymentMethod.id}`);
                          setSelectedPaymentMethodToVerifyMicroTransactions(paymentMethod);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ReactTooltip
        id={'no_pay_permission'}
        place="bottom"
        variant="dark"
        opacity={'100%'}
        style={{
          background: '#222',
          width: '300px',
          zIndex: 99999,
        }}>
        <span>You don't have permissions to make payments. Please contact your account admin.</span>
      </ReactTooltip>
    </div>
  );
};
